import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Option } from '../core-dropdown/core-dropdown.component';
import { Subscription } from 'rxjs';
import { ChipSearchService } from './service/core-chips.service';

@Component({
  selector: 'app-core-chips',
  templateUrl: './core-chips.component.html',
  styleUrl: './core-chips.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoreChipsComponent),
      multi: true,
    },
  ],
})

export class CoreChipsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() options: Option[];
  @Input() value: any[] = [];
  @Input() placeholder: string = '';
  @Input() response: 'both' | 'value' = 'value';
  @Input() version: 'form' | 'ngModel' = 'ngModel';
  @Input() disabled: boolean = false;
  @Input() formGroupParent: FormGroup;
  @Input() formControlName: string;
  @Input() filter: boolean = true;
  @Input() selectAll: boolean = false;
  @Input() size: 'normal' | 'small' = 'normal'
  @Output() valueChange = new EventEmitter<any[]>();
  @Output() changeOn = new EventEmitter<{ clickedValue?: any, allValue: any, deletedValue?: any }>();
  @Output() chipClickedOn = new EventEmitter<any>();
  searchTermSubscription: Subscription;

  isOpen = false;
  searchText = '';
  filteredOptions: any[];
  selectedItems: any[] = [];
  private clickListener: (() => void) | null = null;

  constructor(
    private readonly eRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly searchService: ChipSearchService
  ) {}

  ngOnInit() {
    this.searchTermSubscription = this.searchService.searchTerm$.subscribe((term) => {
      this.highlightSearchResult(term);
    });
    this.filteredOptions = this.options;
    if (this.version === 'form') {
      if (this.formGroupParent && this.formControlName) {
        this.formGroupParent.get(
          this.formControlName
        ) as FormControl;
      }
    } else {
      this.updateSelectedLabel();
    }
  }

  /* Form functions */

  private propagateChange = (_: any) => {
  };
  private propagateTouched = () => {
  };

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    this.value = value;
    this.updateSelectedLabel();
    this.propagateChange(value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && !changes['options'].firstChange) {
      this.filteredOptions = this.options;
      this.updateSelectedLabel();
    }

    if (
      this.version === 'ngModel' &&
      changes['value'] &&
      !changes['value']?.firstChange
    ) {
      this.updateSelectedLabel();
    }
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.addClickListener();
    } else {
      this.removeClickListener();
    }
  }

  reset() {
    this.value = [];
    if (this.version === 'form') {
      this.formGroupParent.patchValue({[this.formControlName]: this.value});
    } else {
      this.valueChange.emit(this.value);
    }
    this.changeOn.emit({allValue: this.value});
    this.selectedItems = [];
  }

  updateSelectedLabel() {
    this.setLabel();
  }

  filterOptions() {
    this.filteredOptions = this.options.filter((option) =>
      option.label.toLowerCase().includes(this.searchText.toLowerCase())
    );
  }

  isSelected(value: any) {
    const values = this.value?.find((selVal) => {
      const val = selVal.value ?? selVal;
      if (val === value) {
        return true;
      }
      return false;
    });
    return values !== undefined
  }

  toggleOption(option: Option) {
    let deletedValue = null;
    if (this.response === 'value') {
      const index = this.value.indexOf(
        (values: Option) => values === option.value
      );
      if (index > -1) {
        //silinecek alanı sure-modal ekle
        deletedValue = this.value[index];
        this.value.splice(index, 1);
      } else {
        this.value.push(option.value);
      }
    } else if (this.response === 'both') {
      const index = this.value.findIndex(
        (values: Option) => values.value === option.value
      );
      if (index > -1) {
        deletedValue = this.value[index];
        this.value.splice(index, 1);
      } else {
        this.value.push(option);
      }
    }

    if (this.version === 'form') {
      this.formGroupParent.patchValue({[this.formControlName]: this.value});
    } else {
      this.valueChange.emit(this.value);
    }
    this.changeOn.emit({allValue: this.value, clickedValue: option, deletedValue});

    this.updateSelectedLabel();
  }

  isAllSelected() {
    for (const val of this.value) {
      const value = val?.value ?? val;
      const isInclude = this.filteredOptions.find(
        (item: Option) => item.value === value
      );
      if (!isInclude) {
        return false;
      }
    }
    return this.filteredOptions.length === this.value.length;
  }


  toggleSelectAll(event: Event) {
    if ((event.target as HTMLInputElement).checked) {
      if (this.response === 'both') {
        this.value = this.filteredOptions.map((option) => option);
      } else {
        this.value = this.filteredOptions.map((option) => option.value);
      }
    } else {
      this.value = [];
    }
    if (this.version === 'form') {
      this.formGroupParent.patchValue({[this.formControlName]: this.value});
    } else {
      this.valueChange.emit(this.value);
    }
    this.changeOn.emit({allValue: this.value});

    this.updateSelectedLabel();
  }

  setLabel() {
    this.selectedItems = [];

    for (const val of this.value || []) {
      const elem = val?.value ?? val;

      for (const option of this.options) {
        if (option.value === elem) {
          this.selectedItems.push(option);
        }
      }
    }
  }


  /*Chip methods */

  clickChip(event: any, data: any) {
    event.stopPropagation();
    this.chipClickedOn.emit(data);
  }

  private addClickListener() {
    this.clickListener = this.renderer.listen(
      'document',
      'click',
      (event: Event) => {
        if (!this.eRef.nativeElement.contains(event.target)) {
          this.isOpen = false;
          this.removeClickListener();
        }
      }
    );
  }

  private removeClickListener() {
    if (this.clickListener) {
      this.clickListener = null;
    }
  }

  highlightSearchResult(text:string){
    if(text){
     this.selectedItems=this.selectedItems.map(item=>{
      return {
        ...item,
        highlight:item.label?.toLocaleLowerCase("tr")?.includes(text.toLocaleLowerCase('tr'))
      }
    })
  }else{
    this.selectedItems=this.selectedItems.map(item=>{
      return {
        ...item,
        highlight:false
      }
    })
  }
  }
  ngOnDestroy() {
    this.removeClickListener();
  }
}
